import Site, { CreateSiteOptions } from '../../interfaces/api/Site';
import api from '../../config/api';
import { CollectionResponse } from '../../interfaces/api/api';
import { getJsonResponse } from '../../utils/ApiUtils';
import useAuth from './useAuth';

const useSite = () => {
  const { fetch } = useAuth();

  const getAllSite = async (name: string, page: number, limit: number): Promise<CollectionResponse<Site>> => {
    const { endpoint, ...options } = api.site.getAll(name, page, limit);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<CollectionResponse<Site>>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const createSite = async (site: CreateSiteOptions): Promise<Site> => {
    const { endpoint, ...options } = api.site.create(site.customerId, site);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Site>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const update = async (id: Site['id'], site: CreateSiteOptions): Promise<Site> => {
    const { endpoint, ...options } = api.site.update(id, site);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Site>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const getSiteOfLegacyCustomer = async (customerId: number) => {
    const { endpoint, ...options } = api.site.getSiteOfLegacyCustomer(customerId);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Site>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const get = async (siteId: string) => {
    const { endpoint, ...options } = api.site.get(siteId);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Site>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const getBatch = async (siteIds: string[]) => {
    const { endpoint, ...options } = api.site.getBatch(siteIds);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<CollectionResponse<Site>>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const addLogo = async (siteId: string, file: File) => {
    const { endpoint, ...options } = api.site.addLogo(siteId, file);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<{ success: boolean; documentId: string }>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const removeLogo = async (siteId: string) => {
    const { endpoint, ...options } = api.site.removeLogo(siteId);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<{ success: boolean }>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const addContact = async (siteId: string, contactId: string) => {
    const { endpoint, ...options } = api.site.addContact(siteId, contactId);

    const response = await fetch(endpoint, options);

    if (!response.ok) {
      throw new Error();
    }
  };

  const revokeAccess = async (siteId: string, customerAccessId: string) => {
    const { endpoint, ...options } = api.site.revokeAccess(siteId, customerAccessId);

    const response = await fetch(endpoint, options);

    if (!response.ok) {
      throw new Error();
    }
  };

  return {
    update,
    createSite,
    getSiteOfLegacyCustomer,
    getAllSite,
    get,
    getBatch,
    addLogo,
    removeLogo,
    addContact,
    revokeAccess,
  };
};

export default useSite;
